import { useEffect, useMemo, useState } from "react";

import { DivergingBarCell, Table } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { BottomBorderHider } from "app/pages/digital-marketing/ad-intelligence/common/StyledComponents";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useAdvertiserActivityDataAndParams } from "../../hooks/useAdvertiserActivityDataAndParams";
import { switcherToDataIndexMap } from "../../shared/hooks/useSwitcherLogic";
import { ShareTypeSwitcher, SwitcherOption } from "../../shared/ShareTypeSwitcher";
import type { AdIntelligenceController } from "../../shared/types/types";
import { NA_VALUE } from "../../utils/utils";
import { usePerformanceQuery } from "../hooks/usePerformanceQuery";
import { getEmptyState } from "../PerformanceWidget";
import { CompareTableWrapper } from "../StyledComponents";
import { calculateCompareEmptyStateHeight } from "../utils/utils";

const columnConfig: ColumnsType = [
    {
        dataIndex: "domain",
        width: 160,
        title: () => translate("ad.intelligence.advertiser.overview.breakdown.widget.domain.title"),
        render: (domain: string) => {
            const favicon = getFaviconUrl(domain);
            const internalLink = displayAdvertisingNavigation.overview.getLink({
                ...proNavigator.getParams(),
                key: domain,
            });

            return (
                <CoreWebsiteCell
                    domain={domain}
                    icon={favicon}
                    textAndIconMargin="8px"
                    showToolTip
                    internalLink={internalLink}
                    childrenCount={0}
                    spacingDomainAndAddons={false}
                    TooltipComponent={({ children }) => (
                        <Tooltip
                            content={translate("ad.intelligence.overview.device.breakdown.table.tooltip", {
                                site: domain,
                            })}
                            placement="top"
                        >
                            {children}
                        </Tooltip>
                    )}
                />
            );
        },
    },
    {
        dataIndex: "deviceData",
        title: () => (
            <Flex
                inline
                justifyContent="space-between"
                width="100%"
            >
                <span>{translate(`ad.intelligence.advertiser.overview.breakdown.widget.desktop.title`)}</span>
                <span>{translate(`ad.intelligence.advertiser.overview.breakdown.widget.mobile.title`)}</span>
            </Flex>
        ),
        render: (value?: { desktopShare: number }) => {
            return value?.desktopShare ? (
                <DivergingBarCell
                    values={[value.desktopShare, 1 - value.desktopShare]}
                    valueFormatter={(value) => (
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[400]}
                            width="42px"
                            display="block"
                        >
                            {percentageSignFilter()(value, 1, NA_VALUE) as string}
                        </Text>
                    )}
                />
            ) : (
                <Flex justifyContent="center">{NA_VALUE}</Flex>
            );
        },
    },
];

interface IDeviceBreakdownCompareProps {
    controller?: AdIntelligenceController;
}

export const DeviceBreakdownCompare = ({ controller = "advertiser" }: IDeviceBreakdownCompareProps) => {
    const params = useAdvertiserActivityDataAndParams();
    const { data, isLoading, error } = usePerformanceQuery(controller);
    const subtitleFilters = useSubtitleFilters("date");
    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState<SwitcherOption>(SwitcherOption.Spend);
    const dataKey = switcherToDataIndexMap[selectedSwitcherOption];
    const keyArray = params.key.split(",");

    const shouldShowEmptyStateForAdIntelMetrics =
        data?.hasAdIntelMetrics === false && selectedSwitcherOption !== SwitcherOption.Visits;

    const dataFormatted = useMemo(() => {
        if (!data?.records || shouldShowEmptyStateForAdIntelMetrics) {
            return [];
        }
        return keyArray.map((domain) => {
            const record = data.records[domain];

            return {
                domain,
                deviceData: record[dataKey],
            };
        });
    }, [data?.records, params.key, shouldShowEmptyStateForAdIntelMetrics, dataKey]);

    useEffect(() => {
        if (data?.hasAdIntelMetrics === false) {
            setSelectedSwitcherOption(SwitcherOption.Visits);
        }
    }, [data?.hasAdIntelMetrics]);

    const competitorCount = keyArray.length || 0;
    const calculatedHeight = calculateCompareEmptyStateHeight(competitorCount);

    const renderEmptyState = getEmptyState(
        !!error,
        true,
        calculatedHeight,
        "ad.intelligence.overview.widget.empty.state.short.subtitle",
    );

    return (
        <Card
            background={colorsPalettes.carbon[0]}
            margin="0 0 24px"
            maxHeight="377px"
            overflow="hidden"
            position="relative"
        >
            <CompareTableWrapper>
                <Table
                    isLoading={isLoading}
                    loaderRowCount={competitorCount}
                    data={dataFormatted}
                    columnConfig={columnConfig}
                    noData={{ Component: () => renderEmptyState }}
                    theme="white"
                    bordersConfig={{ body: false, header: false }}
                >
                    <Table.TableTop withoutRowBorders>
                        <Table.TableTop.Title
                            title={translate(`ad.intelligence.advertiser.overview.breakdown.widget.title`)}
                            tooltip={translate(`ad.intelligence.advertiser.overview.breakdown.widget.title.tooltip`)}
                            filters={subtitleFilters}
                        />
                        {!isLoading ? (
                            <Flex margin="0 0 12px 24px">
                                <ShareTypeSwitcher
                                    selectedSwitcherOption={selectedSwitcherOption}
                                    setSelectedSwitcherOption={setSelectedSwitcherOption}
                                />
                            </Flex>
                        ) : null}
                    </Table.TableTop>
                </Table>
            </CompareTableWrapper>
            <BottomBorderHider />
        </Card>
    );
};
